// src/pages/TradingResources.jsx
//
// Standalone page reached via the main nav "Trading Resources" link.
// Educational material from ODFTrades — PDFs, guides, market study,
// and future learning content. Same visual atmosphere as the rest of
// the site so it feels like part of one platform.
import React, { useState } from 'react';
import ODFLogo from '../components/ODFLogo';
import HeroCanvas from '../components/HeroCanvas';
import HeroAtmosphere from '../components/HeroAtmosphere';
import { ArrowLeft, ArrowRight, Download, CheckCircle2, BookOpen } from 'lucide-react';

const G = '#00e5a0';

export default function TradingResources({ onBack, onContact }) {
  const [pdfEmail,  setPdfEmail]  = useState('');
  const [pdfStatus, setPdfStatus] = useState(''); // '' | 'sending' | 'success' | 'error'

  const handlePdfDownload = async (e) => {
    e.preventDefault();
    if (!pdfEmail || !pdfEmail.includes('@')) {
      setPdfStatus('error');
      return;
    }
    setPdfStatus('sending');
    try {
      await fetch('https://formspree.io/f/mvzlawvw', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pdfEmail,
          source: 'ODF Edge PDF Download (Trading Resources page)',
          subject: 'New PDF Download — ODF Edge',
        }),
      });
    } catch (_) { /* still trigger the download below */ }
    const link = document.createElement('a');
    link.href = '/ODF-Edge.pdf';
    link.download = 'ODF-Edge.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setPdfStatus('success');
  };

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', color: '#f0f0f5', fontFamily: "'IBM Plex Sans', sans-serif", position: 'relative', overflow: 'hidden' }}>
      {/* Ambient atmosphere — matches brand home */}
      <HeroCanvas />
      <HeroAtmosphere />

      {/* Nav */}
      <nav style={{
        position: 'relative', zIndex: 5,
        padding: '24px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 14, flexWrap: 'wrap',
      }}>
        <ODFLogo size="compact" />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={onBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: '1px solid #2a2a3e',
              borderRadius: 100, padding: '8px 16px',
              fontSize: 12, color: '#8888a8', cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
              transition: 'border-color 0.2s, color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = G; e.currentTarget.style.color = '#f0f0f5'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a3e'; e.currentTarget.style.color = '#8888a8'; }}
          >
            <ArrowLeft size={13} /> Back to ODFTrades
          </button>
          <button
            onClick={onContact}
            style={{
              background: G, color: '#0a0a0f', border: 'none',
              borderRadius: 9, padding: '8px 20px',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}
          >
            Contact
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div style={{ position: 'relative', zIndex: 5, padding: '36px 24px 24px', textAlign: 'center', maxWidth: 760, margin: '0 auto' }}>
        <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 16 }}>
          ODFTrades · Trading Resources
        </div>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 600, letterSpacing: '-0.035em', lineHeight: 1.1, margin: '0 0 18px' }}>
          Educational Material for Traders.
        </h1>
        <p style={{ fontSize: 15.5, color: '#8888a8', maxWidth: 600, margin: '0 auto', lineHeight: 1.85 }}>
          PDFs, guides, and educational material from ODFTrades. Starting with the first guide below.
          More on the way.
        </p>
      </div>

      {/* First resource — ODF Edge PDF */}
      <section style={{ position: 'relative', zIndex: 5, padding: '40px 24px 60px', maxWidth: 920, margin: '0 auto' }}>
        <div
          className="pdf-grid"
          style={{
            background: 'linear-gradient(135deg, rgba(0,229,160,0.05), rgba(76,201,240,0.03))',
            border: '1px solid rgba(0,229,160,0.18)',
            borderRadius: 20,
            padding: '48px 42px',
            position: 'relative', overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 200px) 1fr', gap: 38,
            alignItems: 'center',
          }}
        >
          {/* PDF cover */}
          <div
            style={{
              background: 'linear-gradient(145deg, #0a0a0f 0%, #16161f 100%)',
              border: '1px solid rgba(0,229,160,0.25)',
              borderRadius: 10,
              padding: '30px 22px',
              aspectRatio: '3/4',
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(0,229,160,0.08)',
              position: 'relative', overflow: 'hidden',
            }}
          >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${G}, ${G}44)` }} />
            <div>
              <div style={{ fontSize: 9, color: G, fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 10 }}>
                ODFTrades
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#f0f0f5', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                The<br />ODF<br />Edge
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono',monospace", lineHeight: 1.5 }}>
              From Market<br />Confusion to<br />Disciplined<br />Execution
            </div>
          </div>

          {/* Content */}
          <div>
            <div style={{ fontSize: 11, color: G, fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>
              First Resource · PDF
            </div>
            <h3 style={{ fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 600, letterSpacing: '-0.025em', margin: '0 0 12px', lineHeight: 1.2 }}>
              The ODF Edge
            </h3>
            <div style={{ fontSize: 14, color: G, fontWeight: 500, marginBottom: 18, lineHeight: 1.4 }}>
              From Market Confusion to Disciplined Execution
            </div>
            <p style={{ fontSize: 14, color: '#8888a8', lineHeight: 1.85, margin: '0 0 22px' }}>
              A practical guide built from years of real trading experience. Move past the noise, build a
              system that works, and trade with structure.
            </p>

            {pdfStatus === 'success' ? (
              <div style={{
                background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.3)',
                borderRadius: 10, padding: '16px 20px',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,229,160,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: G, flexShrink: 0 }}>
                  <CheckCircle2 size={16} strokeWidth={1.8} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: G, marginBottom: 2 }}>Download started</div>
                  <div style={{ fontSize: 12, color: '#8888a8' }}>Check your downloads folder for ODF-Edge.pdf</div>
                </div>
              </div>
            ) : (
              <form onSubmit={handlePdfDownload} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={pdfEmail}
                    onChange={e => { setPdfEmail(e.target.value); if (pdfStatus === 'error') setPdfStatus(''); }}
                    required
                    style={{
                      flex: '1 1 220px', minWidth: 0,
                      background: '#0a0a0f',
                      border: `1px solid ${pdfStatus === 'error' ? '#ff4d6d' : '#1e1e2e'}`,
                      color: '#f0f0f5', padding: '12px 16px', borderRadius: 9,
                      fontSize: 13, fontFamily: "'IBM Plex Sans',sans-serif", outline: 'none',
                    }}
                  />
                  <button
                    type="submit"
                    disabled={pdfStatus === 'sending'}
                    style={{
                      background: G, color: '#0a0a0f', border: 'none',
                      borderRadius: 9, padding: '12px 22px',
                      fontSize: 13, fontWeight: 700,
                      cursor: pdfStatus === 'sending' ? 'wait' : 'pointer',
                      fontFamily: "'IBM Plex Sans',sans-serif",
                      whiteSpace: 'nowrap',
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    {pdfStatus === 'sending' ? 'Sending...' : <><Download size={14} /> Get the PDF</>}
                  </button>
                </div>
                {pdfStatus === 'error' && (
                  <div style={{ fontSize: 11, color: '#ff4d6d', fontFamily: "'DM Mono',monospace" }}>
                    Please enter a valid email address
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono',monospace" }}>
                  Educational use only · We do not send spam
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Coming soon — placeholder cards for future resources */}
      <section style={{ position: 'relative', zIndex: 5, padding: '40px 24px 80px', maxWidth: 920, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 10 }}>
            More on the Way
          </div>
          <p style={{ fontSize: 14, color: '#8888a8', maxWidth: 540, margin: '0 auto', lineHeight: 1.8 }}>
            Additional guides, market resources, and educational material are being built. Stay close to the
            brand — they release here as they are ready.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
          {[
            { title: 'Market Notes', desc: 'Market study and trade breakdowns shared from ODF as the brand grows.' },
            { title: 'Educational Guides', desc: 'Written material on execution, structure, and trader development.' },
            { title: 'Future Lessons', desc: 'Longer-form content for traders moving toward consistency.' },
          ].map((it, i) => (
            <div key={i} style={{ background: '#0d0d14', border: '1px solid #1a1a26', borderRadius: 12, padding: '22px 20px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 16, right: 16, height: 1, background: `linear-gradient(90deg, transparent, ${G}55, transparent)` }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.18)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: G }}>
                  <BookOpen size={13} strokeWidth={1.8} />
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: '#f0f0f5' }}>{it.title}</div>
                <span style={{
                  fontSize: 9, color: '#8888a8',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid #2a2a3e',
                  borderRadius: 100, padding: '2px 8px',
                  fontFamily: "'DM Mono',monospace", letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                  Coming Soon
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: '#8888a8', lineHeight: 1.65 }}>{it.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer — minimal, matches brand */}
      <footer style={{ position: 'relative', zIndex: 5, borderTop: '1px solid #1a1a26', padding: '28px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14, maxWidth: 1200, margin: '20px auto 0' }}>
        <ODFLogo size="compact" />
        <div style={{ fontSize: 11, color: '#2a2a3e', fontFamily: "'DM Mono', monospace" }}>
          © {new Date().getFullYear()} ODFTrades Inc.
        </div>
      </footer>
    </div>
  );
}
